<template lang="html">
  <div>
    <v-dialog :value="saveDialogOpened" width="600" persistent>
      <v-card class="">
        <v-card-title class="title font-weight-bold"> Save as </v-card-title>

        <v-card-text>
          <v-flex column xs12>
            <v-layout class="mb-2 text-xs-left" column>
              <v-layout align-center>
                <v-text-field
                  v-model="saveAs"
                  hide-details
                  solo
                  class="default-input"
                />
                <span class="subheading ml-3">.{{ documentExtension }}</span>
              </v-layout>
            </v-layout>

            <v-layout class="mt-4">
              <v-spacer class="hidden-xs-only" />
              <v-btn
                class="default-input text-none primary primary--text"
                outline
                @click="saveDialogOpened = false"
                :disabled="saving"
              >
                Cancel
              </v-btn>

              <v-btn
                class="default-input text-none primary"
                :loading="saving"
                :disabled="saving"
                @click="save"
              >
                Save
              </v-btn>
            </v-layout>
          </v-flex>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog
      persistent
      fullscreen
      lazy
      v-model="opened"
      @keydown.esc="$emit('update:opened', false)"
    >
      <v-card flat class="content-container">
        <v-card-title class="title sticky-title pb-0 py-1">
          Edit PDF
          <v-spacer />

          <v-btn
            color="primary"
            class="text-none"
            @click="saveDialogOpened = true"
          >
            Save as ...
          </v-btn>

          <v-btn
            color="darkgrey"
            class="text-none"
            outline
            @click="$emit('update:opened', false)"
          >
            Discard changes
          </v-btn>
        </v-card-title>

        <v-card-text class="dialog-content pa-0">
          <v-layout class="form-container">
            <v-layout column align-center class="sidebar">
              <div v-for="t in thumbnails">
                <img
                  :src="t.thumbnail"
                  :class="t.page === currentPage ? 'thumbnail-selected' : ''"
                  class="thumbnail"
                  @click="currentPage = t.page"
                />
              </div>
            </v-layout>

            <div class="document-container">
              <v-layout class="toolbar" justify-center align-center>
                <div class="d-flex align-center">
                  <v-icon
                    class="grey--text text--darken-1 mr-1"
                    style="cursor: pointer"
                    @click="currentPage -= 1"
                    :disabled="currentPage === 1"
                    large
                    >arrow_left</v-icon
                  >

                  <v-layout
                    class="grey--text text--darken-3 subheading font-weight-bold"
                    align-center
                    shrink
                  >
                    Page:
                    <v-text-field
                      type="number"
                      :value="currentPage"
                      class="page-input mx-2"
                      solo
                      @change="currentPage = parseInt($event)"
                      hide-details
                    />
                    / {{ pageCount }}
                  </v-layout>

                  <v-icon
                    class="grey--text text--darken-1 mx-1"
                    style="cursor: pointer"
                    @click="currentPage += 1"
                    :disabled="currentPage === pageCount"
                    large
                    >arrow_right</v-icon
                  >
                </div>

                <v-divider vertical class="my-2 mx-2" />

                <div class="ml-1">
                  <v-btn
                    flat
                    icon
                    class="icon-button"
                    :disabled="loading"
                    @click="rotatePage"
                  >
                    <v-icon class="material-icons-outlined"
                      >screen_rotation</v-icon
                    >
                  </v-btn>
                  <v-btn
                    flat
                    icon
                    class="icon-button"
                    :disabled="loading"
                    @click="deletePage"
                  >
                    <v-icon class="material-icons-outlined">delete</v-icon>
                  </v-btn>
                </div>
              </v-layout>

              <v-container class="pdf-container">
                <div v-if="loading">
                  <v-progress-circular
                    indeterminate
                    size="50"
                    class="primary--text ma-5"
                  />
                </div>

                <pdf
                  v-else
                  style="box-shadow: 0 1px 3px rgba(0 0 0 / 25%)"
                  :src="pdfData"
                  @num-pages="pageCount = $event"
                  @page-loaded="(currentPage = $event), (pdfLoaded = true)"
                  :page="currentPage"
                />
              </v-container>
            </div>
          </v-layout>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import API from '@/api';
import pdf from 'vue-pdf';
import { generatePdfThumbnails } from '@/utils/pdf';
import { PDFDocument, degrees } from 'pdf-lib';

export default {
  components: {
    pdf,
  },

  props: {
    opened: {
      type: Boolean,
    },

    pdfSrc: {
      type: String,
    },

    path: {
      type: String,
    },

    fileName: {
      type: String,
    },

    currentCompanyId: {
      type: Number,
    },

    questionnaireId: {
      type: Number,
    },
  },

  data() {
    return {
      loading: false,
      saving: false,
      pdfData: '',
      pdfDoc: null,
      pdfLoaded: false,
      currentPage: 1,
      pageCount: 1,
      thumbnails: [],
      saveAs: '',
      saveDialogOpened: false,
    };
  },

  methods: {
    async deletePage() {
      this.pdfDoc.removePage(this.currentPage - 1);
      await this.refresh();
    },

    async rotatePage() {
      const page = this.pdfDoc.getPage(this.currentPage - 1);
      const rotationAngle = page.getRotation().angle;

      page.setRotation(degrees(rotationAngle + 90));
      await this.refresh();
    },

    async refresh() {
      this.loading = true;
      await this.refreshPDF();
      await this.refreshThumbnails();
      this.loading = false;
    },

    async refreshPDF() {
      this.pdfData = await this.pdfDoc.saveAsBase64({ dataUri: true });
      this.pdfDoc = await PDFDocument.load(this.pdfData);
    },

    async refreshThumbnails() {
      this.thumbnails = await generatePdfThumbnails(this.pdfData, 180);
    },

    async save() {
      this.saving = true;
      const {
        data: { id, upload_link },
      } = await API.integration.getTemporaryUploadLink({
        companyId: this.currentCompanyId,
      });

      const file = new Blob([await this.pdfDoc.save()], {
        type: 'application/pdf',
      });

      await API.integration.uploadToDropbox(upload_link, file);

      const response = await API.integration.processDocument({
        id,
        documentName: this.saveAs + '.' + this.documentExtension,
        path: this.path,
        companyId: this.currentCompanyId,
        questionnaireId: this.questionnaireId,
      });

      this.saveDialogOpened = false;
      this.$emit('on-saved');
      this.$emit('update:opened', false);
      this.saving = false;
    },
  },

  computed: {
    documentExtension() {
      return this.fileName.split('.').pop();
    },
  },

  watch: {
    opened: {
      async handler(value) {
        if (value) {
          this.saveAs = this.fileName.includes('(modified)')
            ? this.fileName.replace(`.${this.documentExtension}`, '')
            : this.fileName.replace(`.${this.documentExtension}`, '') +
              ' (modified)';

          const pdfBytes = await fetch(this.pdfSrc).then(res =>
            res.arrayBuffer(),
          );
          this.pdfDoc = await PDFDocument.load(pdfBytes);

          await this.refresh();
        }
      },
    },
  },
};
</script>

<style lang="postcss" scoped>
.content-container {
  height: calc(100vh - 90px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background-color: #ebebeb;
}

.sticky-title {
  position: sticky;
  top: 0;
  left: 0;
  border-bottom: 1px solid #ccc;
  background: #fff;
  z-index: 1;
}

.dialog-content {
  flex: auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.form-container {
  min-height: 0;
}

.sidebar {
  overflow: auto;
  flex: 0;
  min-height: 0;
  min-width: 240px;
  border-right: 1px solid #ccc;
  padding: 20px;
}

.thumbnail {
  cursor: pointer;
  margin-bottom: 32px;
  box-shadow: 0 1px 3px rgba(0 0 0 / 25%);
}

.thumbnail-selected {
  outline: 2px solid #4f7eea;
}

.document-container {
  overflow: auto;
  flex: auto;
  min-height: 0;
}

.pdf-container {
  max-width: 1000px;
}

.toolbar {
  background-color: #ebebeb;
  border-bottom: 1px solid #bbb;
  padding: 8px 16px;
  position: sticky;
  top: 0;
  z-index: 1;
}

.page-input >>> .v-input__control .v-input__slot {
  border-radius: 4px;
  box-shadow: 0px 1px 3px rgba(0 0 0 / 25%) !important;
}

.page-input >>> .v-input__control {
  min-height: 20px;
}

.page-input >>> input {
  width: 32px;
}

.page-input >>> input::-webkit-outer-spin-button,
.page-input >>> input::-webkit-inner-spin-button {
  -webkit-appearance: none;
}
.page-input >>> input[type='number'] {
  -moz-appearance: textfield;
}

.icon-button {
  color: #666;
}
</style>
